// Quan ly API key. Sau khi luu, key chi con hien dang che - khong co duong nao
// xem lai chuoi day du, ke ca goi thang API.

import { useEffect, useState } from "react";
import { api } from "../api";
import { Alert, Badge, ErrorBox, Modal, Spinner, Switch } from "../components/ui";
import type { ApiKey, KeyCheck, Provider } from "../types";

function KetQuaKiemTra({ kq }: { kq: KeyCheck | null }) {
  if (!kq) return <span className="small muted">chưa kiểm tra</span>;
  return (
    <span className="row" style={{ gap: 6 }}>
      <Badge tone={kq.ok ? (kq.het_quota ? "warn" : "ok") : "danger"}>
        {kq.ok ? (kq.het_quota ? "Hết hạn mức" : "Dùng được") : "Hỏng"}
      </Badge>
      <span className="small muted" title={kq.chi_tiet ?? kq.message}>
        {kq.at}
      </span>
    </span>
  );
}

function HangKey({
  provider,
  k,
  onDoi,
}: {
  provider: string;
  k: ApiKey;
  onDoi: () => void;
}) {
  const [dangThu, setDangThu] = useState(false);
  const [loi, setLoi] = useState<unknown>(null);
  const [xacNhanGo, setXacNhanGo] = useState(false);

  const kiemTra = async () => {
    setDangThu(true);
    setLoi(null);
    try {
      await api.kiemTraKeyDaLuu(provider, k.id);
      onDoi();
    } catch (e) {
      setLoi(e);
    } finally {
      setDangThu(false);
    }
  };

  const batTat = async (v: boolean) => {
    try {
      await api.batTatKey(provider, k.id, v);
      onDoi();
    } catch (e) {
      setLoi(e);
    }
  };

  const go = async () => {
    try {
      await api.goKey(provider, k.id);
      setXacNhanGo(false);
      onDoi();
    } catch (e) {
      setLoi(e);
    }
  };

  return (
    <>
      <div className="key-row">
        <span className="key-mask">{k.masked}</span>
        <div className="grow">
          {k.label ? <div style={{ fontWeight: 550 }}>{k.label}</div> : null}
          <div className="small muted">Thêm lúc {k.added_at}</div>
        </div>
        <KetQuaKiemTra kq={k.last_check} />
        <button className="btn btn-sm" onClick={kiemTra} disabled={dangThu}>
          {dangThu ? <Spinner /> : null} Kiểm tra
        </button>
        <Switch checked={k.enabled} onChange={batTat} label={k.enabled ? "Bật" : "Tắt"} />
        <button className="btn btn-sm btn-ghost" onClick={() => setXacNhanGo(true)} title="Gỡ key">
          🗑
        </button>
      </div>

      {loi ? <div style={{ marginBottom: 8 }}><ErrorBox error={loi} /></div> : null}

      {xacNhanGo && (
        <Modal
          title="Gỡ API key"
          onClose={() => setXacNhanGo(false)}
          footer={
            <>
              <button className="btn" onClick={() => setXacNhanGo(false)}>Huỷ</button>
              <button className="btn btn-danger" onClick={go}>Gỡ key</button>
            </>
          }
        >
          <p>
            Gỡ key <span className="key-mask">{k.masked}</span>
            {k.label ? ` (${k.label})` : ""} khỏi kho?
          </p>
          <Alert tone="warn">
            Không lấy lại được chuỗi key từ đây. Muốn dùng lại phải dán lại từ nhà cung cấp.
          </Alert>
        </Modal>
      )}
    </>
  );
}

function ThemKey({ provider, onXong }: { provider: Provider; onXong: () => void }) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [kq, setKq] = useState<KeyCheck | null>(null);
  const [dangThu, setDangThu] = useState(false);
  const [dangLuu, setDangLuu] = useState(false);
  const [loi, setLoi] = useState<unknown>(null);

  const thu = async () => {
    setDangThu(true);
    setLoi(null);
    setKq(null);
    try {
      setKq(await api.thuKey(provider.provider, key));
    } catch (e) {
      setLoi(e);
    } finally {
      setDangThu(false);
    }
  };

  const them = async () => {
    setDangLuu(true);
    setLoi(null);
    try {
      await api.themKey(provider.provider, key, label, true);
      setKey("");
      setLabel("");
      setKq(null);
      onXong();
    } catch (e) {
      setLoi(e);
    } finally {
      setDangLuu(false);
    }
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px dashed var(--border)" }}>
      <div className="param-grid">
        <div className="field param-full">
          <label className="label"><span className="req">*</span>Dán API key mới</label>
          <input
            className="input mono"
            type="password"
            value={key}
            autoComplete="off"
            placeholder={provider.provider === "gemini" ? "AIza…" : "gsk_…"}
            onChange={(e) => {
              setKey(e.target.value);
              setKq(null);
            }}
          />
          <div className="hint">{provider.hint}</div>
        </div>
        <div className="field param-full">
          <label className="label">Tên gợi nhớ (tuỳ chọn)</label>
          <input
            className="input"
            value={label}
            placeholder="ví dụ: tài khoản phụ"
            onChange={(e) => setLabel(e.target.value)}
          />
        </div>
      </div>

      {kq ? (
        <div style={{ marginBottom: 12 }}>
          <Alert tone={kq.ok ? "ok" : "danger"}>
            <strong>{kq.message}</strong>
            {kq.chi_tiet ? <div className="small">{kq.chi_tiet}</div> : null}
            {kq.model_mau?.length ? (
              <div className="small mono" style={{ marginTop: 4 }}>
                {kq.model_mau.slice(0, 3).join(", ")}…
              </div>
            ) : null}
          </Alert>
        </div>
      ) : null}

      {loi ? <div style={{ marginBottom: 12 }}><ErrorBox error={loi} /></div> : null}

      <div className="row">
        <button className="btn" onClick={thu} disabled={!key.trim() || dangThu}>
          {dangThu ? <Spinner /> : null} Kiểm tra key này
        </button>
        <button className="btn btn-primary" onClick={them} disabled={!key.trim() || dangLuu}>
          {dangLuu ? <Spinner /> : null} Thêm vào kho
        </button>
        <span className="small muted">Key hỏng sẽ không được lưu.</span>
      </div>
    </div>
  );
}

export default function KeysPage({ napLaiHealth }: { napLaiHealth: () => void }) {
  const [providers, setProviders] = useState<Record<string, Provider> | null>(null);
  const [loi, setLoi] = useState<unknown>(null);

  const nap = () => {
    api
      .keys()
      .then((r) => {
        setProviders(r.providers);
        napLaiHealth();
      })
      .catch(setLoi);
  };

  useEffect(nap, []);

  if (loi && !providers) return <div className="content-pad"><ErrorBox error={loi} /></div>;
  if (!providers) return <div className="content-pad"><Spinner /></div>;

  return (
    <div className="content-pad">
      <Alert tone="info">
        Key được cất trong kho riêng của backend và bơm vào tiến trình chạy qua biến môi
        trường. Sau khi thêm, giao diện chỉ còn hiển thị bản che — muốn đổi thì gỡ rồi thêm lại.
        Bật nhiều key cho một nhà cung cấp thì hết hạn mức key này pipeline tự xoay sang key kế tiếp.
      </Alert>

      {Object.values(providers).map((p) => (
        <div className="card" key={p.provider} style={{ marginTop: 16 }}>
          <div className="card-head">
            <h2 className="grow">{p.label}</h2>
            <span className="mono muted small">{p.env}</span>
            <Badge tone={p.keys.some((k) => k.enabled) ? "ok" : "default"}>
              {p.keys.filter((k) => k.enabled).length}/{p.keys.length} đang bật
            </Badge>
          </div>
          <div className="card-body">
            {p.keys.length === 0 ? (
              <div className="muted small">Chưa có key nào.</div>
            ) : (
              p.keys.map((k) => (
                <HangKey key={k.id} provider={p.provider} k={k} onDoi={nap} />
              ))
            )}
            <ThemKey provider={p} onXong={nap} />
          </div>
        </div>
      ))}
    </div>
  );
}
